import _ from 'lodash'
import * as React from 'react'

interface PaginationPageInfo {
  kind: 'PaginationPage'
  pageNumber: number
  isCurrentPage: boolean
}

interface PaginationEllipsisInfo {
  kind: 'PaginationEllipsis'
  ellipsisValue: string
}

export interface PrevNextPageLinkProps {
  isDisabled: boolean
  pageNumber: number
}

function createPaginationRange(from: number, to: number): number[] {
  return Array.from(Array(to - from + 1).keys()).map((k) => k + from)
}

function createPaginationEllipsisInfo(value: string): PaginationEllipsisInfo {
  return {
    kind: 'PaginationEllipsis',
    ellipsisValue: value,
  }
}

function createPaginationPageInfo(
  page: number,
  isCurrentPage: boolean,
): PaginationPageInfo {
  return {
    kind: 'PaginationPage',
    pageNumber: page,
    isCurrentPage,
  }
}

export interface PaginationLinkComponentProps {
  pageInfo: PaginationPageInfo
}

function dedupeAndSortPaginationInfo(
  pages: PaginationPageInfo[],
  maxPages: number,
): PaginationPageInfo[] {
  return _.chain(pages)
    .filter(
      (pageInfo) => pageInfo.pageNumber > 0 && pageInfo.pageNumber <= maxPages,
    )
    .uniqBy((pageInfo) => pageInfo.pageNumber)
    .sortBy((pageInfo) => pageInfo.pageNumber)
    .value()
}

function createPaginationPagesAndInsertEllipsis(
  pages: PaginationPageInfo[],
  ellipsis: PaginationEllipsisInfo,
): (PaginationPageInfo | PaginationEllipsisInfo)[] {
  return pages.reduce(
    (
      accumulator: (PaginationPageInfo | PaginationEllipsisInfo)[],
      pageInfo,
    ) => {
      const prevValue = accumulator[accumulator.length - 1]
      if (prevValue && prevValue.kind === 'PaginationPage') {
        const gapBetweenPageNumbers = pageInfo.pageNumber - prevValue.pageNumber
        if (gapBetweenPageNumbers === 2) {
          accumulator.push(
            createPaginationPageInfo(prevValue.pageNumber + 1, false),
          )
        } else if (gapBetweenPageNumbers > 2) {
          accumulator.push(ellipsis)
        }
      }
      accumulator.push(pageInfo)
      return accumulator
    },
    [],
  )
}

interface PaginationLinksProps {
  pageCount: number
  currentPage: number
  PaginationLinkComponent: React.ComponentType<PaginationLinkComponentProps>
  EllipsisComponent: React.ComponentType
  PrevPageLinkComponent: React.ComponentType<PrevNextPageLinkProps>
  NextPageLinkComponent: React.ComponentType<PrevNextPageLinkProps>
}

export function PaginationLinks({
  pageCount,
  currentPage,
  PaginationLinkComponent,
  EllipsisComponent,
  PrevPageLinkComponent,
  NextPageLinkComponent,
}: PaginationLinksProps) {
  const prevPage = currentPage - 1
  const nextPage = currentPage + 1
  const marginGroupLimit = 2
  const firstPageGroup = createPaginationRange(1, marginGroupLimit).map(
    (pageNumber) =>
      createPaginationPageInfo(pageNumber, currentPage === pageNumber),
  )

  const currentPageGroup = createPaginationRange(prevPage, nextPage).map(
    (pageNumber) =>
      createPaginationPageInfo(pageNumber, currentPage === pageNumber),
  )

  const lastPageGroup = createPaginationRange(
    pageCount - marginGroupLimit + 1,
    pageCount,
  ).map((pageNumber) =>
    createPaginationPageInfo(pageNumber, currentPage === pageNumber),
  )

  const ellipsis = createPaginationEllipsisInfo('...')
  const dedupedAndSortedPages = dedupeAndSortPaginationInfo(
    [...firstPageGroup, ...currentPageGroup, ...lastPageGroup],
    pageCount,
  )
  const pages = createPaginationPagesAndInsertEllipsis(
    dedupedAndSortedPages,
    ellipsis,
  )

  return (
    <nav role="navigation" aria-label="Pagination Navigation">
      <ul className="flex space-x-2">
        <PrevPageLinkComponent
          isDisabled={prevPage < 1}
          pageNumber={prevPage}
        />
        {pages.map((p, i) => {
          if (p.kind === 'PaginationPage')
            return <PaginationLinkComponent key={p.pageNumber} pageInfo={p} />
          return <EllipsisComponent key={`ellipsis-${i.toString()}`} />
        })}
        <NextPageLinkComponent
          isDisabled={nextPage > pageCount}
          pageNumber={nextPage}
        />
      </ul>
    </nav>
  )
}
